// Utility to turn a local upload URL into an on-the-fly WebP thumbnail
// served by `serve_upload` in backend/server.py. External URLs (Instagram,
// unsplash, etc.) are returned unchanged since we can't resize them.
//
// Widths must match the `_ALLOWED_WIDTHS` bucket set in the backend or the
// request falls back to the closest bigger bucket. Keep them here in sync.

const LOCAL_UPLOAD_RE = /\/api\/static\/uploads\/[^?#]+/i;

export const ALLOWED_THUMB_WIDTHS = [200, 400, 600, 800, 1200];

// Generic fallback shown when an event has no cover photo. Lives under
// /app/frontend/public and is bundled with the app so we don't hit an
// external CDN.
export const DEFAULT_EVENT_IMAGE = '/panoramas-plancity-default.jpg';

// External placeholders that look like a real cover (HTTP 200 or cached
// via Cloudflare) but are actually the ticketing provider's logo. We
// treat them as "no cover" so the PlanCity default kicks in. Mirrors the
// server-side rule in `backend/services/image_sanitize.py` so both sides
// agree whether an URL counts as a placeholder.
const PLACEHOLDER_URL_RES = [
  // Passline serves 404 HTML (cached as a dark watermark through CDNs)
  // for every URL under `/eventos/`. Match all of them so the PlanCity
  // default cover kicks in whether the slug is empty or not.
  /^https?:\/\/(?:[\w.-]+\.)?passline\.com\/eventos\/[^/?#\s]+\.(?:jpe?g|png|webp)(?:[?#].*)?$/i,
];

/**
 * @param {string | null | undefined} url
 * @returns {boolean}
 */
export function isPlaceholderImage(url) {
  if (!url) return false;
  const s = String(url).trim();
  return PLACEHOLDER_URL_RES.some((re) => re.test(s));
}

/**
 * Returns the event's cover image if present, otherwise the generic
 * PlanCity fallback. Use for the src attribute; callers can still branch
 * their srcset off the raw value if they need to skip responsive variants.
 * @param {string | null | undefined} url
 * @returns {string}
 */
export function eventImageOrDefault(url) {
  if (!url || isPlaceholderImage(url)) return DEFAULT_EVENT_IMAGE;
  return url;
}

/**
 * onError handler that swaps a broken image src for the default PlanCity
 * cover so external CDN URLs that rot don't leave a black hole in the UI.
 * Uses `dataset.fellBack` to avoid infinite error loops if the fallback
 * itself somehow fails.
 */
export function handleEventImageError(e) {
  const img = e.currentTarget;
  if (img.dataset.fellBack) return;
  img.dataset.fellBack = '1';
  img.srcset = '';
  img.src = DEFAULT_EVENT_IMAGE;
}

/**
 * @param {string} url
 * @param {number} width  Requested display width (px). Snaps up to the closest bucket.
 * @returns {string}
 */
export function optimizedImageUrl(url, width = 400) {
  if (!url) return url;
  // Only rewrite URLs we know the backend can serve variants for.
  if (!LOCAL_UPLOAD_RE.test(url)) return url;
  const [base, hash] = url.split('#');
  const sep = base.includes('?') ? '&' : '?';
  return `${base}${sep}w=${width}&fmt=webp${hash ? `#${hash}` : ''}`;
}

/**
 * Build a `srcset` string for responsive images. Covers the two bucket
 * widths that matter for our cards + retina displays.
 * @param {string} url
 * @param {number[]} widths
 */
export function optimizedSrcSet(url, widths = [400, 800]) {
  if (!url || !LOCAL_UPLOAD_RE.test(url)) return undefined;
  return widths
    .map((w) => `${optimizedImageUrl(url, w)} ${w}w`)
    .join(', ');
}
